import {
  checkInstallmentPaymentEligibility,
  getInstallmentPaymentIneligibilityMessage,
  type InstallmentPaymentIneligibilityReason,
} from '@massilia/contracts'
import { type DepositLookupResponse, useGetPublicInstallmentProviderQuery } from '@massilia/queries'
import { RadioGroup } from '@massilia/ui'
import { ArrowLeft, CheckCircle, Info, XCircle } from 'lucide-react'
import { useMemo, useState } from 'react'

import { SummaryRow } from '@/components/payment-confirmation/summary-row'
import { PaymentMethodOption } from '@/components/shared/payment-method-option'
import { centsToEuros, formatPrice } from '@/utils/format-price'

import { DepositCardForm } from './deposit-card-form'
import { DepositInstallmentsForm } from './deposit-installments-form'

interface DepositBookingInfoProps {
  booking: DepositLookupResponse
  credentials: { dossierNumber: string; email: string; lastName: string }
  onBack: () => void
  /**
   * Le client arrive avec `?mode=installments` (bouton du PDF de contrat ou de son espace) :
   * on préselectionne le 3/4x, et surtout on lui explique pourquoi il n'y a plus droit
   * quand l'éligibilité est tombée entre-temps.
   */
  requestedInstallments?: boolean
}

/** Moyens de règlement proposés sur un dossier existant. */
type SettlementMethod = 'card' | 'installments'

export function DepositBookingInfo({
  booking,
  credentials,
  onBack,
  requestedInstallments = false,
}: DepositBookingInfoProps) {
  // Prestataire 3x/4x piloté par le flag backoffice. Le repli SSP/Oney ne vaut que pour
  // l'AFFICHAGE pendant le chargement (c'est la configuration historique, donc le libellé
  // le moins surprenant) : il ne décide de rien. Le serveur relit le flag à l'appel et
  // `DepositInstallmentsForm` transmet `floaPayType` quoi qu'il arrive, pour qu'un désaccord
  // front/serveur ne puisse pas se solder par un refus.
  const { data: providerData } = useGetPublicInstallmentProviderQuery()
  const provider = providerData?.installmentProvider ?? 'SSP'
  const providerLabel = provider === 'FLOA' ? 'FLOA' : 'Oney'

  const paidAmount = useMemo(
    () => booking.payments.reduce((sum, p) => sum + p.amount, 0),
    [booking.payments]
  )

  const remainingAmount = booking.totalPrice - paidAmount
  const isFullyPaid = remainingAmount <= 0
  const isCancelled = booking.status === 'CANCELLED'
  const canPay = !isFullyPaid && !isCancelled

  const isFirstDeposit = booking.payments.length === 0
  const minimumFirstDepositCents = isFirstDeposit ? booking.minimumFirstDepositCents : null

  // Même décision que le serveur (fonction partagée) : le bouton affiché ici ne doit jamais
  // ouvrir un parcours que `POST /payment/initiate-installments` refusera ensuite.
  const eligibility = useMemo(
    () =>
      checkInstallmentPaymentEligibility({
        hasSettledInstallmentPayment: booking.hasSettledInstallmentPayment,
        isCancelled,
        paidCents: paidAmount,
        totalPriceCents: booking.totalPrice,
      }),
    [booking.hasSettledInstallmentPayment, booking.totalPrice, isCancelled, paidAmount]
  )

  // Refus renvoyé par le serveur au moment de l'envoi (l'éligibilité a pu changer entre
  // l'affichage du formulaire et le clic).
  const [serverReason, setServerReason] = useState<InstallmentPaymentIneligibilityReason | null>(
    null
  )

  const ineligibilityReason = serverReason ?? (eligibility.eligible ? null : eligibility.reason)
  // Un dossier neutre se règle par carte uniquement : le fractionné n'est même pas proposé.
  const canUseInstallments = !booking.untracked && ineligibilityReason === null

  const [chosenMethod, setChosenMethod] = useState<SettlementMethod>(() =>
    requestedInstallments && eligibility.eligible ? 'installments' : 'card'
  )
  // Bascule automatique sur la CB dès que le fractionné n'est plus une option.
  const selectedMethod: SettlementMethod = canUseInstallments ? chosenMethod : 'card'

  // On n'explique le refus qu'à ceux qui visaient explicitement le fractionné : pour les
  // autres, l'option est simplement absente.
  const ineligibilityMessage =
    !booking.untracked && ineligibilityReason && (requestedInstallments || serverReason)
      ? getInstallmentPaymentIneligibilityMessage(ineligibilityReason)
      : null

  return (
    <div className="mx-auto w-full max-w-lg">
      <button
        className="mb-4 flex items-center gap-1 text-sm font-medium text-brand-700 hover:underline"
        onClick={onBack}
        type="button"
      >
        <ArrowLeft className="size-4" />
        Rechercher un autre dossier
      </button>

      <div className="flex flex-col gap-5 rounded-2xl border border-gray-light-200 bg-white p-6">
        {/* Booking info header */}
        <div>
          <h2 className="text-xl font-semibold text-gray-light-900">Dossier {booking.reference}</h2>
          {/* Un trajet par voyage : le client qui cherche avec son 2e PNR doit s'y retrouver.
              Repli sur le voyage principal si l'API ne renvoie pas encore les voyages. */}
          {(booking.trips?.length ? booking.trips : [booking]).map((trip) => (
            <p
              className="mt-1 text-sm text-gray-light-600"
              key={`${trip.pnr}-${trip.departureDate}`}
            >
              {trip.departureLocation} → {trip.arrivalLocation}
              {' — '}
              {formatDate(trip.departureDate)}
              {trip.returnDate && ` au ${formatDate(trip.returnDate)}`}
            </p>
          ))}
        </div>

        {/* Payment summary */}
        <div className="flex flex-col gap-2 border-t border-gray-light-200 pt-4">
          <SummaryRow label="Prix total" value={formatPrice(centsToEuros(booking.totalPrice))} />
          {/* Dossier neutre : ses règlements ne sont pas suivis, un solde affiché serait faux. */}
          {!booking.untracked && (
            <>
              <SummaryRow label="Acomptes versés" value={formatPrice(centsToEuros(paidAmount))} />
              <div className="flex items-start justify-between gap-3 text-sm">
                <div className="font-medium text-gray-light-700">Reste à payer</div>
                <div className="text-lg font-bold text-emerald-700">
                  {formatPrice(centsToEuros(remainingAmount))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Payment history */}
        {booking.payments.length > 0 && (
          <div className="flex flex-col gap-2 border-t border-gray-light-200 pt-4">
            <h3 className="text-sm font-semibold text-gray-light-900">Historique des paiements</h3>
            <div className="flex flex-col gap-1">
              {booking.payments.map((payment, index) => (
                <div
                  className="flex items-center justify-between text-sm text-gray-light-700"
                  key={index}
                >
                  <span>{payment.paidAt ? formatDate(payment.paidAt) : 'Date inconnue'}</span>
                  <span className="font-medium">{formatPrice(centsToEuros(payment.amount))}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Status messages */}
        {isFullyPaid && (
          <div className="flex items-center gap-3 rounded-lg bg-emerald-50 p-4">
            <CheckCircle className="size-5 shrink-0 text-emerald-600" />
            <p className="text-sm font-medium text-emerald-800">
              Votre dossier est entièrement réglé. Aucun paiement supplémentaire n&apos;est
              nécessaire.
            </p>
          </div>
        )}

        {isCancelled && (
          <div className="flex items-center gap-3 rounded-lg bg-red-50 p-4">
            <XCircle className="size-5 shrink-0 text-red-600" />
            <p className="text-sm font-medium text-red-800">
              Ce dossier a été annulé. Il n&apos;est plus possible d&apos;effectuer de paiement.
            </p>
          </div>
        )}

        {/* Payment form */}
        {canPay && (
          <div className="flex flex-col gap-4 border-t border-gray-light-200 pt-4">
            <h3 className="text-sm font-semibold text-gray-light-900">
              {canUseInstallments ? 'Comment souhaitez-vous régler ?' : 'Verser un acompte'}
            </h3>

            {/* Fractionné demandé mais refusé : on dit pourquoi, puis on propose la CB. */}
            {ineligibilityMessage && (
              <div className="flex items-start gap-3 rounded-lg bg-amber-50 p-4" role="alert">
                <Info className="mt-0.5 size-5 shrink-0 text-amber-600" />
                <div className="flex flex-col gap-1">
                  <p className="text-sm font-semibold text-amber-900">
                    Paiement en 3/4x indisponible pour ce dossier
                  </p>
                  <p className="text-sm text-amber-800">{ineligibilityMessage}</p>
                  <p className="text-sm text-amber-800">
                    Vous pouvez régler votre dossier par carte bancaire ci-dessous.
                  </p>
                </div>
              </div>
            )}

            {canUseInstallments ? (
              <RadioGroup
                className="flex flex-col gap-0"
                onValueChange={(value) => setChosenMethod(value as SettlementMethod)}
                value={selectedMethod}
              >
                <PaymentMethodOption
                  description=<DepositCardForm
                    credentials={credentials}
                    minimumFirstDepositCents={minimumFirstDepositCents}
                    remainingAmount={remainingAmount}
                  />
                  id="settlement-method-card"
                  isFirst
                  isSelected={selectedMethod === 'card'}
                  label="Payer un acompte en CB"
                  nextIsSelected={selectedMethod === 'installments'}
                  value="card"
                />
                <PaymentMethodOption
                  description={
                    // `remainingAmount` et non `eligibility.amountCents` : les deux valent la
                    // même chose ici (`canUseInstallments` implique une éligibilité), et un
                    // ternaire suggérerait à tort qu'un montant différent est atteignable.
                    // Le montant affiché n'engage rien de toute façon — le serveur le recalcule.
                    <DepositInstallmentsForm
                      amountCents={remainingAmount}
                      credentials={credentials}
                      onIneligible={setServerReason}
                      provider={provider}
                    />
                  }
                  id="settlement-method-installments"
                  isLast
                  isSelected={selectedMethod === 'installments'}
                  label={`Payer en 3/4x avec ${providerLabel} (avec frais)`}
                  prevIsSelected={selectedMethod === 'card'}
                  value="installments"
                />
              </RadioGroup>
            ) : (
              <DepositCardForm
                credentials={credentials}
                minimumFirstDepositCents={minimumFirstDepositCents}
                remainingAmount={remainingAmount}
              />
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function formatDate(isoDate: string) {
  try {
    return new Intl.DateTimeFormat('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(new Date(isoDate))
  } catch {
    return isoDate
  }
}
